import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export function DataProtection() {
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--white)', 
      color: 'var(--dark)',
      padding: '2rem 0'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 2rem' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem' }}>
          <Link 
            to="/" 
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: 'var(--orange)',
              textDecoration: 'none',
              marginBottom: '2rem',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <Shield size={32} style={{ color: 'var(--orange)' }} />
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: '700',
              color: 'var(--dark)'
            }}>
              Recberry s.r.o. Data Protection Policy
            </h1>
          </div>
          
          <p style={{ 
            fontSize: '1.1rem', 
            color: 'var(--gray)', 
            margin: 0,
            lineHeight: '1.6',
            fontWeight: '600'
          }}>
            Version (Effective): October 24, 2025
          </p>
        </div>

        {/* Content */}
        <div style={{ lineHeight: '1.8', fontSize: '1rem' }}>
          
          {/* Introduction */}
          <section style={{ marginBottom: '2.5rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              Your privacy and data protection are our top priorities at Recberry s.r.o. This Data Protection Policy applies to all data collections and processing activities conducted by Recberry s.r.o., particularly through our website, email communications, and marketing services. For purposes of this Policy, any reference to "Recberry," "we," "us," or "our" means Recberry s.r.o. and its affiliates. This Policy describes how we collect, use, share, and otherwise process your personal data in compliance with the General Data Protection Regulation (GDPR) (EU Regulation 2016/679). It also outlines your choices regarding the use, access, correction, and deletion of your personal data. The use of personal data is limited to the purposes outlined herein, such as providing marketing services, sending newsletters, and contacting you as requested.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              We assure you that your personal data is used solely within our company to promote our products and services through activities like newsletters and direct communications. We do not share, sell, or transfer your data to any external parties for their own purposes beyond the necessary service providers outlined in this Policy. You can unsubscribe from our communications or request data deletion at any time, and we will promptly honor such requests.
            </p>
          </section>

          {/* Data Controller */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>1. Data Controller</h2>
            <p style={{ marginBottom: '1rem' }}>
              <strong>Recberry s.r.o.</strong><br/>
              IČ: 04972236 | DIČ: CZ04972236<br/>
              Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic<br/>
              Incorporated in the Commercial Register kept by the Municipal Court in Prague, Section C, File No. 256441<br/>
              <strong>Data Protection Contact:</strong> Barbora Jensik<br/>
              <strong>Email:</strong> barbora@recberry.com
            </p>
          </section>

          {/* Legal Basis */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>2. Legal Basis for Processing</h2>
            <p>
              We process your personal data based on your explicit consent under Article 6(1)(a) of the GDPR. This consent is freely given, specific, informed, and unambiguous. You may withdraw your consent at any time without affecting the lawfulness of processing based on consent before its withdrawal. No automated individual decision-making, including profiling under Article 22 GDPR, occurs in our processing activities.
            </p>
          </section>

          {/* Collected Data */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>3. Collected Personal Data and How We Use It</h2>
            
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>3.1. Data Categories</h3>
            <p style={{ marginBottom: '1rem' }}>
              We may collect and/or process the following categories of personal data from individuals who interact with our services, such as newsletter subscribers, website visitors, or marketing contacts:
            </p>
            
            <div style={{ 
              background: '#f8f9fa', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              marginBottom: '1rem',
              overflowX: 'auto'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Type of Data</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Description</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Duration</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Entity that Collects your Data</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: '600' }}>Purpose of Collection / Processing</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.5rem', fontWeight: '500' }}>Identifiers</td>
                    <td style={{ padding: '0.5rem' }}>Data relating to an identified or identifiable natural person. Examples include names, email addresses, phone numbers, job titles, employers, contact details, and IP addresses.</td>
                    <td style={{ padding: '0.5rem' }}>See Sec. 7</td>
                    <td style={{ padding: '0.5rem' }}>Recberry s.r.o.</td>
                    <td style={{ padding: '0.5rem' }}>See Sec. 3.2.1 – 3.2.6</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.5rem', fontWeight: '500' }}>IT Information</td>
                    <td style={{ padding: '0.5rem' }}>Data relating to an individual's use of technology, including IP addresses, device information, browser type, location data (if consented), and cookies (see Sec. 4).</td>
                    <td style={{ padding: '0.5rem' }}>See Sec. 7</td>
                    <td style={{ padding: '0.5rem' }}>Recberry s.r.o.</td>
                    <td style={{ padding: '0.5rem' }}>See Sec. 3.2.1 – 3.2.6</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.5rem', fontWeight: '500' }}>Special Categories</td>
                    <td style={{ padding: '0.5rem' }}>Sensitive data under Art. 9 GDPR, such as health or ethnic origin, only if explicitly provided and necessary (e.g., for accessibility in marketing events). We minimize collection of this data and process it only with explicit consent.</td>
                    <td style={{ padding: '0.5rem' }}>See Sec. 7</td>
                    <td style={{ padding: '0.5rem' }}>Recberry s.r.o.</td>
                    <td style={{ padding: '0.5rem' }}>See Sec. 3.2.1 – 3.2.6 (with additional safeguards)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>3.2. Purpose of Data Processing</h3>
            <p style={{ marginBottom: '1rem' }}>
              Personal data is collected and used for Recberry's legitimate business purposes as the data controller, including fulfilling contractual obligations and complying with legal requirements. We process data only for the purposes described in this Policy or for which we have obtained your consent. Specifically, we use personal data for:
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>3.2.1.</strong> Providing marketing services, such as contacting you about our products or events.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>3.2.2.</strong> Sending newsletters and promotional materials related to our offerings.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>3.2.3.</strong> Facilitating communications and responding to inquiries.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>3.2.4.</strong> Analyzing trends to improve our services (e.g., website usage statistics).</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>3.2.5.</strong> For data security and compliance purposes, including internal audits.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>3.2.6.</strong> For statistical and analytical purposes, including product enhancement, but only in aggregated or anonymized forms.</li>
            </ul>

            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>3.3. Data Usage and Sharing</h3>
            <p style={{ marginBottom: '1rem' }}>
              Recberry may use, disclose, and publish aggregated and/or anonymized data for statistical, analytical, and service improvement purposes. We employ reasonable measures to prevent re-identification of such data by third parties. Anonymized data may be shared with partners for research, but it does not contain personal identifiers.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              We will only share your personal data with third parties (e.g., marketing service providers) as described in this Policy. We do not sell your personal data to any third parties. See Sec. 6 for details. Recberry does not use personal data for automated decision-making, including profiling (Art. 22 GDPR). Our employees are trained on privacy obligations, subject to confidentiality agreements, and access data only as necessary for their duties.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Recberry may disclose personal data (i) as necessary to provide services or as requested by you, (ii) pursuant to law, as determined by us in our sole discretion, or (iii) by a court order, or (iv) in connection with cookies as described in Section 4 of this Policy. We reserve the right to disclose data when we believe, in our sole discretion, that disclosure is necessary to protect our rights and/or comply with a judicial proceeding, subpoena, court order, or legal process.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Personal data may be disclosed to sub-processors (e.g., email service platforms) that assist in our services. These sub-processors are bound by agreements ensuring at least the same level of protection as required by GDPR. A list of sub-processors is available upon request at barbora@recberry.com. We do not transfer data outside our company for any purposes beyond these essential services.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              In case of a corporate transaction (e.g., merger or acquisition), personal data may be transferred to the relevant party, with notice provided via email or our website. We ensure any such transfer complies with GDPR requirements, including providing you with options to object or withdraw consent.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              You may opt-out of marketing communications at any time by using the "Unsubscribe" link in emails or contacting barbora@recberry.com. Unsubscribing will stop all promotional communications, and we will delete your data upon request unless required to retain it for legal reasons.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              We may track and analyze non-identifiable aggregated information from website visitors for internal improvement purposes only.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              <strong>Marketing:</strong> We may collect email addresses for content delivery related to our products, with opt-in/opt-out options as required by GDPR. All processing is limited to promoting our own products and services.
            </p>
          </section>

          {/* Cookies */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>4. How We Use Cookies and Similar Technologies</h2>
            <p style={{ marginBottom: '1rem' }}>
              Cookies and similar technologies (e.g., pixels, web beacons) are used by Recberry and our partners to analyze trends, administer the website, track user movements, and gather demographic information. We categorize cookies as follows:
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Essential cookies:</strong> Necessary for site functionality (e.g., session management).</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Analytical cookies:</strong> For performance insights (e.g., Google Analytics, anonymized data).</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Marketing cookies:</strong> For personalized content delivery.</li>
            </ul>
            <p>
              We obtain your consent for non-essential cookies via a consent banner on our website, in line with GDPR and ePrivacy Directive requirements. You can manage or withdraw consent at any time through browser settings or by contacting us. For more details, see our Cookie Policy at [insert link if available]. We do not use cookies to share data outside our company beyond anonymized analytics.
            </p>
          </section>

          {/* Access Rights */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>5. Access to Personal Data</h2>
            <p>
              Upon request and within one month (extendable to two months for complex requests, as per Art. 12 GDPR), we will provide information about your personal data we hold, including confirmation of processing, categories of data, purposes, recipients, and storage period. You can request correction, amendment, deletion, or a copy of your data in a structured, commonly used format. Contact barbora@recberry.com for these requests. We will verify your identity before responding and process requests free of charge unless they are manifestly unfounded or excessive.
            </p>
          </section>

          {/* Data Recipients */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>6. Data Recipients and Transfers</h2>
            <p>
              Recipients of your personal data are limited to sub-processors providing marketing services (e.g., email platforms) under strict GDPR-compliant data processing agreements. We have no intention to transfer data to non-EU countries or international organizations without adequate safeguards, such as Standard Contractual Clauses approved by the European Commission. All transfers, if any, are documented and ensure an equivalent level of protection. We do not share data with any other third parties for their independent use.
            </p>
          </section>

          {/* Data Retention */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>7. Data Retention</h2>
            <p>
              Personal data is retained for up to 5 years from the date of collection or until you withdraw your consent, whichever occurs first. We retain data only as long as necessary for the purposes for which it was collected, to comply with legal obligations (e.g., tax or accounting requirements), resolve disputes, or enforce agreements. Upon fulfillment of these purposes, we securely delete or anonymize the data. If deletion is not possible (e.g., due to backups), we will inform you and restrict further processing.
            </p>
          </section>

          {/* Data Security */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>8. Data Security</h2>
            <p style={{ marginBottom: '1rem' }}>
              We employ reasonable and appropriate security measures to protect against loss, misuse, unauthorized access, disclosure, alteration, or destruction of personal data. These include:
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Technical measures:</strong> Encryption of data in transit and at rest, firewalls, secure servers.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Organizational measures:</strong> Access controls (role-based), employee training on GDPR compliance, confidentiality agreements.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Physical measures:</strong> Secure facilities for any physical records.</li>
            </ul>
            <p>
              You are responsible for keeping any credentials secure. No method of transmission or storage is 100% secure; however, we regularly review and update our measures to align with industry standards. If you have questions about security, contact barbora@recberry.com.
            </p>
          </section>

          {/* Third Party Links */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>9. Links to Third-Party Sites</h2>
            <p>
              Our website may contain links to third-party sites whose privacy practices differ from ours. If you submit personal data to these sites, it is governed by their policies, and we are not responsible for their actions. We encourage you to review third-party privacy policies before interacting.
            </p>
          </section>

          {/* Minors */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>10. No Services for Minors</h2>
            <p>
              Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal data from minors. If we become aware of such collection, we will take steps to delete the data promptly.
            </p>
          </section>

          {/* Your Rights */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>11. Your Rights Under GDPR</h2>
            <p style={{ marginBottom: '1rem' }}>
              In accordance with GDPR, you have the following rights regarding your personal data:
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Access (Art. 15):</strong> Obtain confirmation of processing and a copy of your data.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate or incomplete data.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Erasure ("Right to be Forgotten," Art. 17):</strong> Request deletion when data is no longer necessary or consent is withdrawn.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Restriction of Processing (Art. 18):</strong> Limit processing under certain conditions (e.g., while verifying accuracy).</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Object (Art. 21):</strong> Object to processing for direct marketing or based on legitimate interests.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format and transmit it to another controller.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right to Withdraw Consent (Art. 7):</strong> Revoke consent at any time, without affecting prior lawfulness.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Right Not to Be Subject to Automated Decisions (Art. 22):</strong> Not applicable, as we do not engage in such processing.</li>
            </ul>
            <p>
              To exercise these rights, contact barbora@recberry.com. We will respond within one month. You also have the right to lodge a complaint with a supervisory authority, such as the Czech Office for Personal Data Protection (Úřad pro ochranu osobních údajů, www.uoou.cz).
            </p>
          </section>

          {/* GDPR Compliance */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>12. GDPR Compliance Checklist</h2>
            <p style={{ marginBottom: '1rem' }}>
              To demonstrate our commitment to GDPR compliance, we maintain the following practices:
            </p>
            <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Lawfulness, Fairness, and Transparency:</strong> All processing is based on consent and clearly explained in this Policy.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Purpose Limitation:</strong> Data is used only for specified marketing purposes related to our products.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Data Minimization:</strong> We collect only necessary data (e.g., name and email for newsletters).</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Accuracy:</strong> We update data upon request and verify sources.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Storage Limitation:</strong> Retention is limited to 5 years or less, with regular reviews.</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Integrity and Confidentiality:</strong> Security measures protect against breaches; incidents are reported within 72 hours to authorities if required (Art. 33).</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Accountability:</strong> We maintain records of processing activities (Art. 30), conduct data protection impact assessments for high-risk activities (Art. 35), and appoint a data protection contact (Barbora Jensik).</li>
            </ul>
            <p>
              We conduct annual internal audits to ensure ongoing compliance.
            </p>
          </section>

          {/* Policy Changes */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>13. Changes to This Policy</h2>
            <p>
              We may update this Policy to reflect changes in our practices or legal requirements. Material changes will be notified via email or a prominent notice on our website at least 30 days before they take effect. We encourage you to review this Policy periodically.
            </p>
          </section>

          {/* Data Transfers */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>14. Data Transfers Outside the EEA</h2>
            <p>
              Personal data is processed within the EEA. If any transfers outside the EEA are necessary (e.g., for sub-processors), we use Standard Contractual Clauses or other approved mechanisms to ensure adequate protection, as required by Chapter V of the GDPR.
            </p>
          </section>

          {/* Compliance */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>15. Compliance and Supervisory Authority</h2>
            <p>
              We comply fully with GDPR and are registered with the relevant authorities. You have the right to lodge a complaint with the Czech Office for Personal Data Protection (www.uoou.cz) if you believe your rights have been infringed.
            </p>
          </section>

          {/* Contact */}
          <section style={{ 
            background: 'var(--light-gray)', 
            padding: '2rem', 
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>16. Data Protection Contact</h2>
            <p style={{ marginBottom: '1rem' }}>
              For any questions, concerns, or to exercise your rights, contact:
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Barbora Jensik</strong><br/>
              <strong>Email:</strong> barbora@recberry.com<br/>
              <strong>Address:</strong> Recberry s.r.o., Tupolevova 741, 199 00 Prague 9 - Letňany, Czech Republic
            </p>
          </section>

          {/* Last Updated */}
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem 0', 
            borderTop: '1px solid var(--light-gray)',
            color: 'var(--gray)',
            fontSize: '0.9rem'
          }}>
            <p style={{ margin: 0 }}>
              Version (Effective): October 24, 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}